import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { BrainCircuit, TrendingUp, Search, RefreshCcw, Lightbulb, CheckCircle2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { PageHeader, Spinner, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { api, errMsg, fmtINR } from "../../lib/api";
import { useRoleModules } from "../../lib/roleModules";
import { NoAccessPanel } from "../../components/FeatureGate";

const RISK_STYLE = {
  low: "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  high: "bg-red-50 text-red-700 border-red-200",
};

const CONFIDENCE_STYLE = {
  high: "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};

function ForecastSection({ code }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get(`/t/${code}/ai/forecast`)
      .then((r) => setData(r.data))
      .catch((e) => setError(errMsg(e)))
      .finally(() => setLoading(false));
  }, [code]);

  useEffect(() => { load(); }, [load]);

  const chartData = data ? [
    ...(data.context?.monthly_history || []).map((m) => ({ month: m.month, Actual: m.amount })),
    ...(data.forecast || []).map((f) => ({ month: f.month, Projected: f.projected_amount_inr })),
  ] : [];

  return (
    <Card className="shadow-sm" data-testid="ai-forecast-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Revenue Forecast (next 6 months)
            </CardTitle>
            <CardDescription>AI projection based on demand register and monthly collection history</CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="forecast-refresh-button">
            <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3 py-4" data-testid="forecast-loading">
            <p className="text-sm text-muted-foreground">Generating AI forecast... this can take a few seconds</p>
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : error ? (
          <EmptyState title="Could not generate forecast" subtitle={error}
            action={<Button size="sm" onClick={load}>Retry</Button>} />
        ) : data && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="border rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Total demand</p>
                <p className="font-semibold tabular-nums" data-testid="forecast-total-demand">{fmtINR(data.context?.total_demand)}</p>
              </div>
              <div className="border rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Collected</p>
                <p className="font-semibold tabular-nums text-emerald-700">{fmtINR(data.context?.collected)}</p>
              </div>
              <div className="border rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Expected FY collection</p>
                <p className="font-semibold tabular-nums">{data.expected_fy_collection_rate_pct}%</p>
              </div>
              <div className="border rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Risk level</p>
                <Badge variant="outline" className={`${RISK_STYLE[data.risk_level] || ""} capitalize mt-0.5`} data-testid="forecast-risk-badge">
                  {data.risk_level}
                </Badge>
              </div>
            </div>

            <div className="h-64" data-testid="forecast-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(210 25% 90%)" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `\u20B9${(v / 1000).toFixed(0)}k`} width={52} />
                  <Tooltip formatter={(v) => fmtINR(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Actual" fill="hsl(206 88% 28%)" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Projected" fill="hsl(206 60% 62%)" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-4">
                <p className="text-sm font-medium flex items-center gap-2 mb-2"><Lightbulb className="h-4 w-4" /> Insights</p>
                <ul className="space-y-1.5 text-sm text-muted-foreground list-disc pl-4" data-testid="forecast-insights">
                  {(data.insights || []).map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
              <div className="border rounded-lg p-4">
                <p className="text-sm font-medium flex items-center gap-2 mb-2"><CheckCircle2 className="h-4 w-4" /> Recommendations</p>
                <ul className="space-y-1.5 text-sm text-muted-foreground list-disc pl-4" data-testid="forecast-recommendations">
                  {(data.recommendations || []).map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ValuationSection({ code, readOnly = false }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [running, setRunning] = useState(false);
  const [valuation, setValuation] = useState(null);

  const doSearch = async (e) => {
    e?.preventDefault();
    try {
      const r = await api.get(`/t/${code}/properties`, { params: { search, limit: 8 } });
      setResults(r.data.items || []);
      if ((r.data.items || []).length === 0) toast.info("No properties match this search");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const run = async () => {
    if (!selected) return;
    setRunning(true);
    setValuation(null);
    try {
      const r = await api.post(`/t/${code}/ai/valuation`, { property_id: selected.id });
      setValuation(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="shadow-sm" data-testid="ai-valuation-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Building2 className="h-4 w-4" /> AI Property Valuation
        </CardTitle>
        <CardDescription>Estimate fair market value and suggested annual tax for any property</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={doSearch} className="flex gap-2">
          <Input placeholder="Search by PIN, owner or address..." value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="valuation-search-input" />
          <Button type="submit" variant="outline" data-testid="valuation-search-button"><Search className="h-4 w-4" /></Button>
        </form>

        {results.length > 0 && (
          <div className="space-y-1.5" data-testid="valuation-search-results">
            {results.map((p) => (
              <button key={p.id} onClick={() => { setSelected(p); setResults([]); setValuation(null); }}
                className="w-full text-left border rounded-lg px-3 py-2 hover:bg-muted/60 flex items-center justify-between gap-2"
                data-testid={`valuation-result-${p.pin}`}>
                <span><span className="font-mono text-sm font-medium">{p.pin}</span>
                  <span className="text-sm text-muted-foreground ml-2">{p.owner_name}</span></span>
                <span className="text-xs text-muted-foreground">{p.ward_label}</span>
              </button>
            ))}
          </div>
        )}

        {selected && (
          <div className="border rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="valuation-selected-property">
            <div>
              <p className="font-mono text-sm font-semibold">{selected.pin}</p>
              <p className="text-sm">{selected.owner_name} <span className="text-muted-foreground">• {selected.address}</span></p>
            </div>
            {readOnly ? (
              <span className="text-xs text-muted-foreground" data-testid="run-valuation-locked">View only — running a valuation needs full access</span>
            ) : (
            <Button onClick={run} disabled={running} data-testid="run-valuation-button">
              <BrainCircuit className="h-4 w-4 mr-2" /> {running ? "Analyzing..." : "Run AI valuation"}
            </Button>
            )}
          </div>
        )}

        {running && (
          <div className="space-y-2" data-testid="valuation-loading">
            <p className="text-sm text-muted-foreground">AI is evaluating locality, category, area and demand history...</p>
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {valuation && (
          <div className="space-y-4" data-testid="valuation-result">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="border rounded-lg p-4 sm:col-span-1">
                <p className="text-xs text-muted-foreground">Estimated market value</p>
                <p className="text-xl font-semibold tabular-nums font-heading" data-testid="valuation-market-value">
                  {fmtINR(valuation.estimated_market_value_inr)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Range {fmtINR(valuation.value_range_low_inr)} – {fmtINR(valuation.value_range_high_inr)}
                </p>
              </div>
              <div className="border rounded-lg p-4">
                <p className="text-xs text-muted-foreground">Suggested annual tax</p>
                <p className="text-xl font-semibold tabular-nums font-heading">{fmtINR(valuation.suggested_annual_tax_inr)}</p>
                <p className="text-xs text-muted-foreground mt-1">Current demand: {fmtINR(valuation.property?.current_annual_demand)}</p>
              </div>
              <div className="border rounded-lg p-4">
                <p className="text-xs text-muted-foreground">Confidence</p>
                <Badge variant="outline" className={`${CONFIDENCE_STYLE[valuation.confidence] || ""} capitalize mt-1`} data-testid="valuation-confidence">
                  {valuation.confidence}
                </Badge>
              </div>
            </div>
            <div className="border rounded-lg p-4">
              <p className="text-sm font-medium mb-1">Rationale</p>
              <p className="text-sm text-muted-foreground">{valuation.rationale}</p>
              {(valuation.factors || []).length > 0 && (
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground list-disc pl-4">
                  {valuation.factors.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AiInsights() {
  const { code } = useParams();
  // Phase 152: "ai_insights.forecast" and "ai_insights.valuation" sub-features
  const access = useRoleModules(code);
  const showForecast = access.featureAllowed("ai_insights", "forecast");
  const showValuation = access.featureAllowed("ai_insights", "valuation");
  return (
    <div>
      <PageHeader title="AI Insights" subtitle="Revenue forecasting and property valuation powered by AI" />
      {access.loaded && !showForecast && !showValuation ? <NoAccessPanel what="AI Insights" testId="ai-insights-no-access" /> : (
      <div className="space-y-5">
        {showForecast && <ForecastSection code={code} />}
        {showValuation && <ValuationSection code={code} readOnly={!access.canWrite("ai_insights", "valuation")} />}
      </div>
      )}
    </div>
  );
}
